import styles from "./CustomCardAndChatBot.module.scss";
import CardList from "@components/molecules/CardListPrice";
import ChatbotSupport from "@components/molecules/ChatbotSupport";
import { PageType } from "@constants/pageTypes";
import { useEffect, useState } from "react";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { addUTMParamsToLeadData } from "@utils/utmParams";
import { postLead } from "@services/leadCaptureService";
import { getLeadData, setLeadData } from "@utils/leadDataStorage";
import { getPlatform } from "@utils/platformDetection.client";


export default function CustomCardAndChatBot({
  variants,
  isLoggedIn,
  city,
  carName,
  brandName,
  modelName,
  pageType,
  carAvailability,
  savingBalance,
  firstVariantPriceBreakup,
}: {
  variants: any[];
  isLoggedIn: boolean;
  city: any;
  carName?: string;
  brandName: string;
  modelName: string;
  pageType?: PageType;
  carAvailability?: string;
  savingBalance?: any;
  firstVariantPriceBreakup?: any;
}) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);
  const [showMore, setShowMore] = useState(false);
  const [jsEnabled, setJsEnabled] = useState(false);
  
  const variantsToShow = variants;

  // Mark JS as enabled when component mounts
  useEffect(() => {
    setJsEnabled(true);
  }, []);
  const handleToggle = async (index: number) => {
    const prevIdx = expandedIndex;
    const variantName = variantsToShow?.[index]?.variantName;
    setExpandedIndex((prev: number | null) => (prev === index ? null : index));
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageType,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_variant",
        car_availability: carAvailability,
        vehicle_make: brandName,
        vehicle_model: modelName,
        vehicle_variant: variantName,
        city_name: city?.name,
        Section: "Price page summary",
      },
    });
    if (prevIdx !== index && isLoggedIn) {
      let leadData: any = {
        city: city?.name,
        make: brandName || "",
        model: modelName || "",
        variant: variantName || "",
        color: "",
      };
      // const storedSource = sessionStorage.getItem("referrerSource");
      const utmTerm =
        new URL(window.location.href).searchParams.get("utm_term") || "";
      leadData.dump_data = {
        mx_Platform: getPlatform(),
        mx_Page_Type_Web: pageType,
        mx_Login_Type: "Viewed detailed price",
        // SourceReferrerURL: storedSource,
        mx_URL: window.location.href || "https://ackodrive.com",
        mx_UTM_Term: utmTerm,
        mx_color: leadData?.color || "",
      };

      // Get existing lead data with fallback to localStorage
      const existingLeadData = await getLeadData();
      const mergedLeadData = { ...existingLeadData, ...leadData };
      leadData = mergedLeadData;
      
      // Save to both storages
      setLeadData(mergedLeadData);
      const buyingPeriod = leadData?.buying_period || "";
      try {
        const leadResponse = await postLead(addUTMParamsToLeadData(leadData));
        if (!leadResponse.success) {
          console.error("Lead capture failed.", leadResponse);
          await trackEventWithCommonProps("exception", EventType.TRACK_EVENT, {
            pageType: pageType,
            additionalProps: {
              exception_type: "API issue",
              exception_name: "failed lead creation",
              car_availability: carAvailability,
              vehicle_make: brandName,
              vehicle_model: modelName,
              vehicle_variant: variantName,
              city_name: city?.name,
              login_type: "Viewed detailed price",
              intent: buyingPeriod,
            },
          });
        } else {
          const leadCreated = !!leadResponse?.data?.lead_id;
          await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
            pageType: pageType,
            additionalProps: {
              action_type: leadCreated ? "lead_created" : "lead_updated",
              car_availability: carAvailability,
              vehicle_make: brandName,
              vehicle_model: modelName,
              vehicle_variant: variantName,
              city_name: city?.name,
              login_type: "Viewed detailed price",
              intent: buyingPeriod,
            },
          });
        }
      } catch (error) {
        console.error("Error in postLead API call:", error);
        await trackEventWithCommonProps("exception", EventType.TRACK_EVENT, {
          pageType: pageType,
          additionalProps: {
            exception_type: "API issue",
            exception_name: "failed lead creation",
            car_availability: carAvailability,
            vehicle_make: brandName,
            vehicle_model: modelName,
            vehicle_variant: variantName,
            city_name: city.name,
            login_type: "Viewed detailed price",
            intent: buyingPeriod
          },
        });
      }
    }
  };
  return (
    <div className={styles.container}>
      <CardList
        variants={variants}
        isLoggedIn={isLoggedIn}
        city={city}
        brandName={brandName}
        modelName={modelName}
        pageType={pageType}
        carAvailability={carAvailability}
        savingBalance={savingBalance}
        expandedIndex={expandedIndex}
        showMore={showMore}
        setShowMore={setShowMore}
        jsEnabled={jsEnabled}
        variantsToShow={variantsToShow}
        handleToggle={handleToggle}
        firstVariantPriceBreakup={firstVariantPriceBreakup}
      />
      <div className={styles.chatWrapper}>
        <ChatbotSupport
          heading="Not sure which variant suits you?"
          subheading="No worries, we’ll help you with your question"
          buttonText="Find the right variant"
          icon={`/icons/chatbot.svg`}
          cityName={city.name}
          customChatbotStyling={styles.customChatbotStyling}
          chatBotPrompt={`Which variant should I buy in ${carName}?`}
          pageType={pageType}
          carAvailability={carAvailability}
          brandName={brandName}
          modelName={modelName}
        />
      </div>
    </div>
  );
}
